// dependencies
import produce from "immer";

// config
import Config from "./Config";

// schema
import { FieldFilter, FieldValue, FieldValueObject, FilterComparison, InputValue } from "../../schemas/report";

const isObjectWithValue = (possibleObj: InputValue): possibleObj is FieldValueObject => {
	if (
		typeof possibleObj === "object" &&
		!(possibleObj instanceof Date) &&
		!Array.isArray(possibleObj) &&
		possibleObj &&
		Object.keys(possibleObj).includes("value") &&
		possibleObj.value !== ""
	) {
		return true;
	} else {
		return false;
	}
};

const isValueComparison = (value: FieldValue): value is FilterComparison => {
	if (typeof value === "string" && Config.comparisonValueLookup[value]) {
		return true;
	}
	return false;
};

export type InputType = "field" | "comparison" | "value";

/**
 * Takes a filter group, (an array of filters), a value, a type, and an index of
 * a filter in that group, and returns the filter group with the value updated
 * on the filter at the given index if it is valid for the type
 */
export const handleSetInput = (
	inputType: InputType,
	inputValue: InputValue,
	filterGroup: FieldFilter[],
	conditionItemIndex: number
): FieldFilter[] => {
	const filterCopy = produce(filterGroup, (draft) => {
		switch (inputType) {
			case "field":
				if (isObjectWithValue(inputValue)) {
					// sets all inputs to match field type
					// on field change, resets to clean state
					draft.unshift({
						field: inputValue.value?.toString() ?? "",
						type: inputValue.type ?? "",
						category: draft[conditionItemIndex].category,
						comparison: inputValue.type && Config.fieldTypeOptionsLookup[inputValue.type][0].value,
						value: "",
					});
					draft.splice(1);
				}
				break;
			case "comparison":
				if (isObjectWithValue(inputValue) && isValueComparison(inputValue.value)) {
					draft[conditionItemIndex].comparison = inputValue.value;
				}
				break;
			case "value":
				if (isObjectWithValue(inputValue)) {
					draft[conditionItemIndex].value = inputValue.value as FieldValue;
				} else if (Array.isArray(inputValue)) {
					draft[conditionItemIndex].value = inputValue.map((value) => {
						if (isObjectWithValue(value)) {
							return value.value as string | Date;
						} else {
							return value as FieldValue;
						}
					}) as FieldValue;
				} else {
					draft[conditionItemIndex].value = inputValue as FieldValue;
				}
				break;
		}
	});
	return filterCopy;
};
